// ==========================================================================
// Air Quality Card - US AQI hero card for the weather tab
// Background color encodes the AQI band (mirrors alert-banner severity colors)
// ==========================================================================

import { useTranslation } from 'react-i18next';
import { getAqiCategoryColor } from '@/utils/airQuality';
import type { AirQuality, AirQualityPollutants } from '@/types/weather.types';

interface AirQualityCardProps {
  airQuality: AirQuality;
}

const POLLUTANT_CHIPS: { key: keyof AirQualityPollutants; label: string }[] = [
  { key: 'pm2_5', label: 'PM2.5' },
  { key: 'pm10', label: 'PM10' },
  { key: 'ozone', label: 'O₃' },
  { key: 'nitrogen_dioxide', label: 'NO₂' },
];

export function AirQualityCard({ airQuality }: AirQualityCardProps) {
  const { t } = useTranslation();
  const { us_aqi, category, category_label, guidance, pollutants } = airQuality;

  const chips = POLLUTANT_CHIPS.flatMap(({ key, label }) => {
    const pollutant = pollutants?.[key];
    if (pollutant?.value == null) return [];
    return [{ key, label, value: pollutant.value, unit: pollutant.unit }];
  });

  return (
    <div
      className="rounded-2xl p-5 text-white mb-4"
      style={{ backgroundColor: getAqiCategoryColor(category) }}
      data-testid="air-quality-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="bg-white/20 rounded-full px-3 py-1">
          <span className="text-sm font-medium">{t('weather.airQuality')}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-3xl">💨</span>
          {us_aqi != null && (
            <span className="text-4xl font-bold">{Math.round(us_aqi)}</span>
          )}
        </div>
      </div>

      {category_label && (
        <p className="text-sm font-semibold mb-1">{category_label}</p>
      )}
      {guidance && (
        <p className="text-white/90 text-sm leading-relaxed">{guidance}</p>
      )}

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {chips.map((chip) => (
            <div key={chip.key} className="bg-white/20 rounded-full px-3 py-1">
              <span className="text-xs font-medium">
                {chip.label} {chip.value} {chip.unit}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
